/**
 * ProtectedRoute — auth gate for any route that requires a logged-in user.
 *
 * Replaces the ad-hoc `if (!user) navigate("/login")` pattern that lived in
 * 3+ pages (CoachingSessionPage, CapScanReportPage, MovementReportPage).
 *
 * Behaviour:
 *   • While AuthContext is still hydrating (loading=true) → render a small
 *     placeholder so the child page doesn't fire requests against `null`.
 *   • If auth resolves to no user → redirect to /login, preserving the
 *     originating URL in `state.from` so the login flow can return them.
 *   • If user resolves → render children verbatim.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="font-dm flex items-center justify-center min-h-[40vh] text-navy/60" data-testid="protected-route-loading">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return children;
}
